//codex-- Track an in-flight create synchronously to block repeated submissions before React renders the loading state.
import React, { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
//codex-- Reuse the existing spinner and toast UI for create progress and unexpected submit failures.
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-toastify';
//codex-- Personal template controls are enabled explicitly by the operation schedule page for new records.
import OperationTemplateControls from './OperationTemplateControls';

const OperationFormModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  technicians,
  handleDelete,
  isEditing,
  //codex-- Preserve other consumers of this shared modal until they opt into private templates.
  disabled,
  enableTemplates = false,
  templateApiUrl
}) => {
  const [isMultiTechOpen, setIsMultiTechOpen] = useState(false);
  //codex-- Await the parent's existing create request; validation/failure restores the form, and success keeps its existing close behavior.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pendingSubmit = useRef(false);
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (pendingSubmit.current) return;
    if (formData.id || isEditing) return onSubmit(event);
    pendingSubmit.current = true;
    setIsSubmitting(true);
    try {
      await onSubmit(event);
    } catch (error) {
      toast.error('Unable to create schedule. Please try again.', { position: 'top-center' });
    } finally {
      pendingSubmit.current = false;
      setIsSubmitting(false);
    }
  };
  //codex-- Keep the modal open while its create request is pending, including Escape/backdrop and Cancel actions.
  const handleClose = () => {
    if (!isMultiTechOpen && !pendingSubmit.current) onClose();
  };

const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData((prev) => ({
    ...prev,
    [name]: value,
    ...(name === 'tech_id'
      ? { multi_tech_ids: [] }
      : {}),
  }));
};


  const openMultiTechModal = () => {
  setFormData((prev) => ({
    ...prev,
    tech_id: '',
    tech_name: '',
  }));
  setIsMultiTechOpen(true);
};

  const handleMultiTechChange = (techId) => {
    //codex-- Template IDs may be strings while the live technician list uses numbers; compare their values consistently.
    setFormData((prev) => {
      const current = prev.multi_tech_ids || [];
      const exists = current.some((id) => String(id) === String(techId));

      return {
        ...prev,
        multi_tech_ids: exists
          ? current.filter((id) => String(id) !== String(techId))
          : [...current, techId],
      };
    });
  };

  const handleSelectAllTechs = () => {
    setFormData((prev) => ({
      ...prev,
      multi_tech_ids: technicians.map((tech) => tech.id),
    }));
  };

  const handleClearAllTechs = () => {
    setFormData((prev) => ({
      ...prev,
      multi_tech_ids: [],
    }));
  };  

  const selectedMultiTechs = technicians.filter((tech) =>
    (formData.multi_tech_ids || []).includes(String(tech.id)) ||
    (formData.multi_tech_ids || []).includes(tech.id)
  );

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-20"
            //codex-- Apply the same pending-create guard to dialog dismissal.
            onClose={handleClose}
          >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/30 backdrop-blur-sm">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95 translate-y-4"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-4"
              >
                <Dialog.Panel className="relative mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
                  <div className="flex items-center justify-between border-b border-gray-200 bg-[#90d824] px-6 py-4">
                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                      {formData.id ? 'Edit Operation Schedule' : 'New Operation Schedule'}
                    </Dialog.Title>
                  </div>

                  {/* //codex-- Disable all form controls together while creating, preserving their values for an error retry. */}
                  <form onSubmit={handleFormSubmit} aria-label="Operation schedule" aria-busy={isSubmitting} className="px-6 py-5 mt-4 space-y-4">
                    <fieldset disabled={isSubmitting} className="min-w-0 space-y-4">
                    {/* //codex-- Save/select private templates only in the New Schedule flow. */}
                    {enableTemplates && isOpen && !isEditing && !formData.id && <OperationTemplateControls apiUrl={templateApiUrl} formData={formData} setFormData={setFormData} />}
                    <div>
                      <label className="block text-sm font-medium">Description</label>
                      <input
                        type="text"
                        name="description"
                        value={formData.description || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium">Start Date</label>
                        <input
                          type="date"
                          name="schedule_from"
                          value={formData.schedule_from || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border rounded py-2 px-3"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium">End Date</label>
                        <input
                          type="date"
                          name="schedule_to"
                          value={formData.schedule_to || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border rounded py-2 px-3"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium">Start Time</label>
                        <input
                          type="time"
                          name="start_time"
                          value={formData.start_time || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border rounded py-2 px-3"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium">End Time</label>
                        <input
                          type="time"
                          name="end_time"
                          value={formData.end_time || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border rounded py-2 px-3"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium">Status</label>
                        <select
                          name="status"
                          value={formData.status || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border rounded py-2 px-3"
                          required
                        >
                          <option value="">Select…</option>
                          <option>Scheduled</option>
                          <option>In Progress</option>
                          <option>Completed</option>
                          <option>Cancelled</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium">Date</label>
                        <input
                          type="date"
                          name="date"
                          value={formData.date || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border rounded py-2 px-3"
                          disabled
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium">Time Log</label>
                        <input
                          type="time"
                          name="time_log"
                          value={formData.time_log || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border rounded py-2 px-3"
                          disabled
                        />
                      </div>
                    </div>

                    <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">Technician</label>

                    {!formData.id && (
                      // Only show these options when creating a new schedule, not when editing to avoid users 
                      // multiple techs by accident when they just want to change the description or something
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleSelectAllTechs}
                        className="text-sm font-medium text-green-600 hover:text-green-800"
                      >
                        Select All
                      </button>

                      <button
                        type="button"
                        onClick={handleClearAllTechs}
                        className="text-sm font-medium text-red-600 hover:text-red-800"
                      >
                        Clear All
                      </button>

                      <button
                        type="button"
                        onClick={openMultiTechModal}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        + Multiple Techs
                      </button>
                    </div>
                    )}
                    </div>
                        <select
                          name="tech_id"
                          value={formData.tech_id || ''}
                          onChange={handleInputChange}
                          disabled={(formData.multi_tech_ids || []).length > 0}
                          className={`mt-1 block w-full rounded py-2 px-3 border ${
                            (formData.multi_tech_ids || []).length > 0
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : ''
                          }`}
                        >
                        <option value="">Select Technician</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.first_name} {tech.last_name}
                          </option>
                        ))}
                      </select>
                        {(formData.multi_tech_ids || []).length > 0 && (
                          <p className="mt-1 text-xs text-gray-500">
                            Technician dropdown is disabled while multiple technicians are selected.
                          </p>
                        )}                      

                      {selectedMultiTechs.length > 0 && (
                        <div className="mt-3 rounded border bg-gray-50 p-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Selected Additional Techs:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {selectedMultiTechs.map((tech) => (
                              <span
                                key={tech.id}
                                className="inline-flex items-center rounded-full bg-[#90d824] px-3 py-1 text-xs font-medium text-white"
                              >
                                {tech.first_name} {tech.last_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium">User Comment</label>
                      <textarea
                        name="user_comment"
                        value={formData.user_comment || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                        rows={3}
                      />
                    </div>

                    <div className="mt-6 flex justify-end space-x-2">
                      <button
                        type="button"
                        //codex-- Prevent cancellation during an in-flight create request.
                        onClick={handleClose}
                        className="inline-flex justify-center rounded-md border border-transparent bg-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                      {/* //codex-- Hide Create while saving instead of leaving a clickable submit control under the overlay. */}
                      {!isSubmitting && <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border border-transparent bg-[#90d824] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#679c18]"
                      >
                        {formData.id ? 'Update' : 'Create'}
                      </button>}
                    </div>
                    </fieldset>
                  </form>
                  {/* //codex-- Cover the modal with accessible loading feedback until the parent's create request finishes. */}
                  {isSubmitting && <div role="status" aria-live="polite" className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                    <div className="rounded-xl border border-slate-200 bg-white px-6 py-4 text-sm font-semibold text-slate-700 shadow-lg"><LoadingSpinner>Creating schedule...</LoadingSpinner></div>
                  </div>}
                  {/* Multiple Tech Modal */}
                  {isMultiTechOpen && (
                  <div
                      className="absolute inset-0 z-40 flex items-center justify-center bg-black/40"
                      onClick={() => setIsMultiTechOpen(false)}
                    >
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                      <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">
                          Select Multiple Technicians
                        </h2>
                        <button
                          type="button"
                          onClick={() => setIsMultiTechOpen(false)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Close
                        </button>
                      </div>

                      <div className="max-h-72 overflow-y-auto space-y-2">
                        {technicians.map((tech) => {
                          const checked =
                            (formData.multi_tech_ids || []).includes(String(tech.id)) ||
                            (formData.multi_tech_ids || []).includes(tech.id);

                          return (
                            <div
                              key={tech.id}
                              className="flex items-center gap-3 rounded border p-3 hover:bg-gray-50"
                            >
                              <input
                                id={`multi-tech-${tech.id}`}
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleMultiTechChange(tech.id)}
                                className="h-4 w-4"
                              />
                              <label
                                htmlFor={`multi-tech-${tech.id}`}
                                className="cursor-pointer select-none"
                              >
                                {tech.first_name} {tech.last_name}
                              </label>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-5 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsMultiTechOpen(false)}
                          className="rounded-md bg-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-400"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>
                  )}                    
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>                  
        </Dialog>
        
      </Transition>
    </>
  );
};

export default OperationFormModal;
